import { useRef, useState, useCallback, useEffect } from 'react';
import { compose } from 'redux';
import withStyles from '@material-ui/styles/withStyles';

import moment from 'moment';
import Highcharts from 'highcharts';
import gantt from 'highcharts/highcharts-gantt';
import HighchartsReact from 'highcharts-react-official';

import { Grid, Typography } from '@material-ui/core';
import highchartsTheme from 'layout/highcharts.theme';
import winchHighchartsTheme from 'features/projects/dashboard/components/dashboards/winch/winch.highcharts.theme';
import themes, { defaultThemeType } from 'layout/themes';
import {
  ACTIVITY_LOG_STATUSES,
  ACTIVITY_TYPES,
  Filters,
} from 'features/wells/overview/activityLog/helpers/activityLog.constants';
import { getRichEditorText } from 'utils/app.util';
import { isMomentObject } from 'features/wells/overview/activityLog/helpers/activityLog.validators';

const theme = themes[defaultThemeType];

const { fontColor, defaultFontSize } = winchHighchartsTheme.style;

const FieldActivityLogsGanttChart = ({
  classes,
  activityLogs,
  filter,
  chartBackgroundColor = theme.altus.background.dashboard,
  showDefaultButtons = true,
  showNavigatorChart = true,
}) => {
  const chartComponent = useRef(null);

  useEffect(() => {
    if (chartComponent?.current) {
      const chart = chartComponent?.current?.chart;
      if (chart?.xAxis) {
        chart.xAxis[0]?.setExtremes(
          isMomentObject(filter[Filters.FROM])
            ? filter[Filters.FROM].valueOf()
            : moment(filter[Filters.FROM]).valueOf(),
          isMomentObject(filter[Filters.TO])
            ? filter[Filters.TO].valueOf()
            : moment(filter[Filters.TO]).valueOf(),
          true,
          true,
        );
      }
    }
  }, [filter, activityLogs]);

  useEffect(() => {
    const chart = chartComponent.current.chart;
    const updateTodayLine = () => {
      const now = Date.now();
      chart.xAxis[0].removePlotLine('today-line');
      chart.xAxis[0].addPlotLine({
        id: 'today-line',
        value: now,
        color: theme.palette.error.main,
        width: 2,
        zIndex: 5,
        label: {
          text: 'Today',
          rotation: 0,
          align: 'center',
          x: -5,
          y: -100,
          style: {
            color: theme.palette.error.main,
            whiteSpace: 'nowrap',
          },
        },
      });
    };

    updateTodayLine();
    const timer = setInterval(updateTodayLine, 60 * 60 * 1000); // every hour

    return () => clearInterval(timer);
  }, []);

  const updateSeriesData = useCallback((activityLogs) => {
    const chart = chartComponent.current.chart;
    if (!chart) return;

    while (chart.series.length) {
      chart.series[0].remove(false);
    }

    const logsArray = activityLogs?.toJS?.() ?? [];

    // Group by wellbore to show parent
    const groupedByWellbore = logsArray.reduce((acc, log) => {
      const key = log.wellboreId;
      if (!acc[key]) {
        acc[key] = {
          wellboreId: key,
          wellboreName: log.wellboreName,
          activities: [],
        };
      }
      acc[key].activities.push(log);
      return acc;
    }, {});

    // Build one flat array of activities: [ parent, ...children, parent2, ... ]
    const ganttTasks = Object.values(groupedByWellbore).flatMap((group) => {
      const parentTask = {
        id: `wellbore-${group.wellboreId}`,
        name: group.wellboreName,
        collapsed: false,
      };

      const childTasks = group.activities.map((log) => ({
        id: log.activityLogId,
        name: log.activityName,
        parent: parentTask.id,
        start: log.startDate ? moment(log.startDate).valueOf() : null,
        end: log.endDate ? moment(log.endDate).valueOf() : null,
        activityType: ACTIVITY_TYPES.find((t) => t.id === log.activityType)
          .name,
        description: getRichEditorText(log.description),
        status: ACTIVITY_LOG_STATUSES.find((t) => t.id === log.status).name,
        responsible: log.responsibleUser?.fullName,
        comment: getRichEditorText(log.comment),
        mitigationSolutions: getRichEditorText(log.mitigationSolutions),
        showCheckbox: false,
      }));

      return [parentTask, ...childTasks];
    });

    //Add a single Gantt series with all tasks
    chart.addSeries(
      {
        name: 'Wellbore Activities',
        type: 'gantt',
        data: ganttTasks,
      },
      false,
    );

    chart.redraw();
    chart.reflow();
  }, []);

  useEffect(() => {
    updateSeriesData(activityLogs);
  }, [activityLogs, updateSeriesData]);

  const [chartOptions] = useState(
    Highcharts.merge(false, highchartsTheme, {
      chart: {
        backgroundColor: chartBackgroundColor,
      },
      rangeSelector: {
        inputEnabled: false,
        enabled: showDefaultButtons,
        selected: 6,
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
        buttons: showDefaultButtons
          ? [
              {
                type: 'minute',
                count: 1,
                text: '1mn',
                title: 'View 1 minute',
              },
              {
                type: 'month',
                count: 1,
                text: '1m',
                title: 'View 1 month',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(1, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'month',
                count: 3,
                text: '3m',
                title: 'View 3 months',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(3, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'month',
                count: 6,
                text: '6m',
                title: 'View 6 months',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(6, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'ytd',
                count: 2,
                text: 'YTD',
                title: 'View year to date',
              },
              {
                type: 'year',
                count: 1,
                text: '1y',
                title: 'View 1 year',
              },
              {
                type: 'all',
                text: 'All',
                title: 'View all',
              },
            ]
          : [],
      },
      scrollbar: {
        enabled: true,
      },
      navigator: {
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
        enabled: showNavigatorChart,
        liveRedraw: true,
        series: {
          type: 'gantt',
          pointPlacement: 0.5,
          pointPadding: 0.25,
          accessibility: {
            enabled: false,
          },
        },
      },
      xAxis: {
        plotBands: {
          color: fontColor,
        },
        showEmpty: false,
        tickColor: fontColor,
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
      },
      yAxis: {
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
      },
      accessibility: {
        keyboardNavigation: {
          seriesNavigation: {
            mode: 'serialize',
          },
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function () {
          const point = this;
          if (!point.parent) {
            return `
                            <b>Wellbore:</b> ${point.name}<br/>
                        `;
          }
          return `
                    <b>${point.name}  (${point.activityType})</b><br/>
                    ${point.description}<br/>
                    Start: ${Highcharts.dateFormat(
                      '%d-%m-%Y',
                      point.start,
                    )}    End: ${Highcharts.dateFormat(
            '%d-%m-%Y',
            point.end,
          )}<br/>
                    Status: ${point.status}<br/>
                    Responsible: ${point.responsible}<br/>
                    Comment: <br/>
                    ${point.comment}<br/>
                    Solutions for Mitigating Challenges:<br/>
                    ${point.mitigationSolutions}
                    `;
        },
      },
    }),
  );

  return (
    <>
      {!activityLogs?.size && (
        <Grid container>
          <Grid item xs={12} className={classes.noContent}>
            <Typography variant="body2">
              No Activity found for the applied filters.
            </Typography>
          </Grid>
        </Grid>
      )}
      <HighchartsReact
        ref={chartComponent}
        constructorType={'ganttChart'}
        highcharts={gantt}
        options={chartOptions}
      />
    </>
  );
};

const styles = (theme) => ({
  noContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
});

export default compose(withStyles(styles))(FieldActivityLogsGanttChart);
